import { Component } from '@theme/component';
import { ThemeEvents, OverlayOpenedEvent, eventBus } from '@theme/events';

/**
 * @typedef {Object} OverlayRefs
 * @property {HTMLDialogElement} dialog - The dialog element
 */

/**
 * Generic overlay component using native <dialog> element.
 *
 * Supports two display modes:
 * - **drawer** (default): Slides in from left or right edge.
 * - **modal**: Centered dialog with scale + opacity animation.
 *
 * Features:
 * - Native focus trapping (via dialog)
 * - Escape key to close (native)
 * - Click backdrop to close
 * - Slide-in animation for drawer / scale animation for modal
 * - Focus restoration on close
 *
 * @example
 * <overlay-component id="my-overlay" data-position="left">
 *   <div on:click="/close" class="overlay__backdrop"></div>
 *   <dialog ref="dialog" class="overlay-drawer" on:click="/handleDialogClick">...</dialog>
 * </overlay-component>
 *
 * // Open the overlay
 * document.querySelector('#my-overlay').open();
 *
 * @extends {Component<OverlayRefs>}
 */
class OverlayComponent extends Component {
	/** @type {HTMLElement | null} */
	#previousActiveElement = null;

	/** @type {AbortController | null} */
	#abortController = null;

	connectedCallback() {
		super.connectedCallback();

		if (!this.refs.dialog) return;

		this.#abortController = new AbortController();
		const { signal } = this.#abortController;

		// Intercept Escape key: prevent native dialog.close() and run our
		// animated close() instead. The cancel event fires before close on Escape.
		this.refs.dialog.addEventListener('cancel', (e) => {
			e.preventDefault();
			this.close();
		}, { signal });

		// Native dialog close event (programmatic close or form[method=dialog])
		this.refs.dialog.addEventListener('close', () => {
			this.#onClose();
		}, { signal });

		// Manual Escape key handling for non-modal (below-header) mode
		// (show() dialogs don't fire cancel on Escape)
		if (this.belowHeader) {
			document.addEventListener('keydown', (e) => {
				if (e.key === 'Escape' && this.refs.dialog?.hasAttribute('open')) {
					this.close();
				}
			}, { signal });
		}

		// Mutual exclusion: close when another surface (overlay, mega menu, search) opens
		document.addEventListener(ThemeEvents.overlayOpened, (e) => {
			if (e.detail?.source !== this && this.refs.dialog?.hasAttribute('open')) {
				this.close();
			}
		}, { signal });

		this.#setupBottomSheetDrag(signal);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#abortController?.abort();
		this.#abortController = null;
	}

	/**
	 * Close the overlay — bound via on:click="/close" on backdrop and close button
	 */
	close() {
		if (!this.refs.dialog) return;

		// Remove open attribute to trigger close animation
		this.refs.dialog.removeAttribute('data-open');

		// Wait for animation to complete before closing
		const durationStr = getComputedStyle(this.refs.dialog).getPropertyValue('--overlay-animation-duration')?.trim() || '0.3s';
		const ms = parseFloat(durationStr) * (durationStr.includes('ms') ? 1 : 1000) || 300;

		setTimeout(() => {
			this.refs.dialog?.close();
		}, ms);
	}

	/**
	 * Open the overlay
	 */
	open() {
		if (!this.refs.dialog) return;

		this.#previousActiveElement = document.activeElement;

		// Use show() for below-header mode to allow header interaction
		// Use showModal() for full-page modal behavior
		if (this.belowHeader) {
			this.refs.dialog.show();
		} else {
			this.refs.dialog.showModal();
		}

		// Add open attribute for CSS animations
		this.refs.dialog.setAttribute('data-open', '');

		// Move focus into the dialog so it stays trapped, but prefer an
		// unobtrusive target — the close button — so users don't see a
		// focus ring on the first checkbox/input inside the content.
		// Falls back to the first focusable in content, then the dialog
		// itself, only if no close button is rendered.
		requestAnimationFrame(() => {
			// On mobile, move focus to the dialog container itself (tabindex="-1")
			// so no control shows a focus ring on open — looks cleaner on touch.
			// Keyboard users still Tab into the close button (which keeps its ring).
			if (window.matchMedia('(max-width: 749px)').matches) {
				this.refs.dialog?.focus({ preventScroll: true });
				return;
			}

			const closeButton = this.refs.dialog?.querySelector('.overlay-drawer__close, .overlay-dialog__close');
			if (closeButton instanceof HTMLElement) {
				closeButton.focus({ preventScroll: true });
				return;
			}
			const contentSelector = this.mode === 'modal' ? '.overlay-dialog__content' : '.overlay-drawer__content';
			const firstFocusable = this.refs.dialog?.querySelector(
				`${contentSelector} button, ${contentSelector} [href], ${contentSelector} input, ${contentSelector} select, ${contentSelector} textarea, ${contentSelector} [tabindex]:not([tabindex="-1"])`
			);
			if (firstFocusable instanceof HTMLElement) {
				firstFocusable.focus({ preventScroll: true });
			}
		});

		// Notify other surfaces to close (mutual exclusion)
		eventBus.emit(new OverlayOpenedEvent({ source: this }));
	}

	/**
	 * Toggle the overlay
	 */
	toggle() {
		if (this.refs.dialog?.hasAttribute('open')) {
			this.close();
		} else {
			this.open();
		}
	}

	/**
	 * Handle click on dialog element — closes if click is on the dialog itself (not content).
	 * Bound via on:click="/handleDialogClick" on the dialog.
	 * @param {MouseEvent} event
	 */
	handleDialogClick(event) {
		// The declarative event system proxies event.target to the element with the
		// on:click attribute (the dialog). Use the original target from composedPath
		// to check if the click was on the dialog itself vs. its content.
		const actualTarget = event.composedPath()[0];
		if (actualTarget === this.refs.dialog) {
			this.close();
		}
	}

	/**
	 * Mobile bottom-sheet: drag the handle down to dismiss. Active only when the
	 * drawer opted into `data-mobile-bottom-sheet` and the mobile layout is shown.
	 * Past a threshold the sheet closes; otherwise it snaps back to open.
	 * @param {AbortSignal} signal
	 */
	#setupBottomSheetDrag(signal) {
		const handle = this.refs.sheetHandle;
		if (!handle) return;

		const container = this.refs.dialog?.querySelector('.overlay-drawer__container');
		if (!container) return;

		let startY = 0;
		let deltaY = 0;
		let dragging = false;

		handle.addEventListener('pointerdown', (event) => {
			if (!window.matchMedia('(max-width: 749px)').matches) return;
			dragging = true;
			deltaY = 0;
			startY = event.clientY;
			container.style.transition = 'none';
			handle.setPointerCapture?.(event.pointerId);
		}, { signal });

		handle.addEventListener('pointermove', (event) => {
			if (!dragging) return;
			deltaY = Math.max(0, event.clientY - startY);
			container.style.transform = `translateY(${deltaY}px)`;
		}, { signal });

		const endDrag = () => {
			if (!dragging) return;
			dragging = false;
			container.style.transition = '';
			const threshold = Math.min(container.offsetHeight * 0.25, 120);
			if (deltaY > threshold) {
				// Slide the rest of the way down, then close.
				container.style.transform = 'translateY(100%)';
				this.close();
			} else {
				// Snap back: clear the inline transform so the CSS open state restores it.
				container.style.transform = '';
			}
		};

		handle.addEventListener('pointerup', endDrag, { signal });
		handle.addEventListener('pointercancel', endDrag, { signal });
	}

	/**
	 * Called when dialog closes
	 */
	#onClose() {
		// Ensure data-open is removed (handles native Escape close which
		// bypasses close() and its animation-then-remove flow)
		this.refs.dialog?.removeAttribute('data-open');

		// Clear any inline transform/transition left by a bottom-sheet drag so
		// the next open starts from a clean state.
		const container = this.refs.dialog?.querySelector('.overlay-drawer__container');
		if (container) {
			container.style.transform = '';
			container.style.transition = '';
		}

		// Restore focus to previous element
		if (this.#previousActiveElement instanceof HTMLElement) {
			this.#previousActiveElement.focus();
		}
	}

	/**
	 * Display mode: 'drawer' (slide from edge) or 'modal' (centered)
	 * @returns {string}
	 */
	get mode() {
		return this.dataset.mode || 'drawer';
	}

	/**
	 * Position getter (left or right) — applies to drawer mode only
	 * @returns {string}
	 */
	get position() {
		return this.dataset.position || 'left';
	}

	/**
	 * Whether overlay is in below-header mode
	 * @returns {boolean}
	 */
	get belowHeader() {
		return this.hasAttribute('data-below-header');
	}
}

if (!customElements.get('overlay-component')) {
	customElements.define('overlay-component', OverlayComponent);
}
